import { notification, Button, Space, Tag } from 'antd';
import {
  InfoCircleOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { SEVERITY_CONFIG, ALERT_TYPE_CONFIG, type EventAlert, type AlertSeverity } from '../../api/alertsService';

type NotificationAPI = ReturnType<typeof notification.useNotification>[0];

function getSeverityIcon(severity: AlertSeverity) {
  switch (severity) {
    case 'info':
      return <InfoCircleOutlined style={{ color: SEVERITY_CONFIG.info.color }} />;
    case 'warning':
      return <WarningOutlined style={{ color: SEVERITY_CONFIG.warning.color }} />;
    case 'critical':
      return <ExclamationCircleOutlined style={{ color: SEVERITY_CONFIG.critical.color }} />;
    default:
      return <InfoCircleOutlined />;
  }
}

export interface ShowAlertToastOptions {
  api: NotificationAPI;
  alert: EventAlert;
  notificationKey: string;
  compact: boolean;
  duration: number;
  triggerLabel?: string;
  onAcknowledge?: () => void;
  onClose?: () => void;
}

export function showAlertToast({
  api,
  alert,
  notificationKey,
  compact,
  duration,
  triggerLabel = '',
  onAcknowledge,
  onClose,
}: ShowAlertToastOptions) {
  const typeConfig = ALERT_TYPE_CONFIG[alert.alert_type];
  const sevColor = SEVERITY_CONFIG[alert.severity]?.color || '#1890ff';

  const ackButton = alert.requires_acknowledgment && onAcknowledge ? (
    <Button
      type="primary"
      size="small"
      icon={<CheckOutlined />}
      onClick={onAcknowledge}
      style={compact ? { fontSize: 11, height: 22, padding: '0 6px' } : undefined}
    >
      {compact ? 'Ack' : 'Acknowledge'}
    </Button>
  ) : undefined;

  api.open({
    key: notificationKey,
    title: (
      <Space size={compact ? 4 : 8}>
        <Tag color={sevColor} style={compact ? { margin: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px' } : undefined}>
          {alert.severity.toUpperCase()}
        </Tag>
        <span style={{ fontWeight: 600, fontSize: compact ? 12 : undefined }}>{alert.title}</span>
      </Space>
    ),
    description: (
      <div style={compact ? { fontSize: 11 } : undefined}>
        <p style={{ margin: compact ? '4px 0' : '8px 0', fontSize: compact ? 11 : undefined }}>{alert.message}</p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <Space size={compact ? 4 : 8} style={{ fontSize: compact ? 10 : 12, opacity: 0.75 }} wrap>
            <span>{typeConfig?.icon} {typeConfig?.label}{triggerLabel}</span>
            {alert.sessions?.length > 0 && alert.sessions.map(session => (
              <Tag key={session} style={compact ? { fontSize: 10, lineHeight: '16px', padding: '0 4px', margin: 0 } : undefined}>
                {session}
              </Tag>
            ))}
          </Space>
          {compact && ackButton}
        </div>
      </div>
    ),
    icon: getSeverityIcon(alert.severity),
    duration,
    placement: 'topRight',
    actions: compact ? undefined : ackButton,
    onClose,
    style: {
      borderLeft: `${compact ? 3 : 4}px solid ${sevColor}`,
      maxWidth: 'calc(100vw - 24px)',
      ...(compact && { padding: '6px 12px' }),
    },
  });
}
